import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaHeart,
  FaUserMd,
  FaAward,
  FaUsers,
  FaArrowRight,
  FaWhatsapp,
  FaPhone,
  FaComments,
} from "react-icons/fa";
import {
  HELPDESK_PHONE_TEL,
  WHATSAPP_NUMBER,
} from "../../utils/clinicContact";
import "./Home.css";

const Home = () => {
  const stats = [
    { icon: <FaHeart />, number: "15+", label: "Years Experience" },
    { icon: <FaUserMd />, number: "10,000+", label: "Patients Treated" },
    { icon: <FaAward />, number: "100%", label: "Patient Satisfaction" },
    { icon: <FaUsers />, number: "24/7", label: "Emergency Care" },
  ];

  const services = [
    {
      title: "Angiography & Angioplasty",
      description:
        "Advanced X-ray imaging and minimally invasive procedures to treat blocked arteries",
      icon: <FaHeart />,
    },
    {
      title: "Stent Placement & TAVR",
      description:
        "Modern techniques for artery support and aortic valve replacement without surgery",
      icon: <FaHeart />,
    },
    {
      title: "Valvular Heart Disease",
      description:
        "Comprehensive treatment for damaged heart valves and structural heart conditions",
      icon: <FaHeart />,
    },
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <motion.div
            className="hero-text"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 style={{ fontSize: "2.2rem" }}>
              Dr. Satya Sudhish Nimmagadda{" "}
            </h1>
            <h2 style={{ fontSize: "1.2rem" }}>INTERVENTIONAL CARDIOLOGIST</h2>
            <p
              className="qualification"
              style={{ fontSize: "var(--font-size-base)" }}
            >
              MD General Medicine, DM Cardiology
            </p>
            <p
              className="specialization"
              style={{ fontSize: "var(--font-size-base)" }}
            >
              Expert in Interventional Cardiology, Preventive Care & Heart
              Health Management
            </p>
            <div className="hero-cta">
              <Link to="/contact" className="btn btn-primary">
                Book Appointment
              </Link>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                <FaWhatsapp /> WhatsApp Connect
              </a>
            </div>
          </motion.div>
          <motion.div
            className="hero-image"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="doctor-photo">
              <img
                src={require("../../assets/main-pic-doc.jpg")}
                alt="Dr. Satya"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="welcome">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 style={{ fontSize: "1.8rem" }}>
              Welcome to SATYA HEART CLINIC
            </h2>
            <p>
              We are committed to providing exceptional cardiac care with the
              latest medical technology and personalized treatment plans. Our
              mission is to ensure your heart health through comprehensive
              diagnosis, treatment, and preventive care.
            </p>
            <Link to="/about" className="btn btn-outline">
              Learn More About Us <FaArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="services-preview">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 style={{ fontSize: "1.6rem" }}>Our Cardiology Services</h2>
            <div className="services-grid">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  className="service-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <div className="service-icon">{service.icon}</div>
                  <h3 style={{ fontSize: "1.3rem" }}>{service.title}</h3>
                  <p>{service.description}</p>
                </motion.div>
              ))}
            </div>
            <div className="services-cta">
              <Link to="/services" className="btn btn-primary">
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats">
        <div className="container">
          <motion.div
            className="stats-grid"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-item"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Happy Patients Section */}
      <section className="happy-patients">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 style={{ fontSize: "1.6rem" }}>What Our Happy Patients Say</h2>
            <div className="testimonials-preview">
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <p>
                    "Dr. Satya is an exceptional cardiologist. His expertise and
                    caring approach made me feel confident about my treatment."
                  </p>
                  <div className="testimonial-author">
                    <strong>Ramesh Kumar</strong>
                    <span>Hyderabad</span>
                  </div>
                </div>
              </div>
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <p>
                    "The best heart care I've ever received. Dr. Satya's clinic
                    is professional and patient-friendly."
                  </p>
                  <div className="testimonial-author">
                    <strong>Priya Sharma</strong>
                    <span>Kukatpally</span>
                  </div>
                </div>
              </div>
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <p>
                    "Excellent treatment and follow-up care. Dr. Satya truly
                    cares about his patients' well-being."
                  </p>
                  <div className="testimonial-author">
                    <strong>Venkat Reddy</strong>
                    <span>Hyderabad</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="testimonials-cta">
              <Link to="/testimonials" className="btn">
                <FaComments />
                Discover More Patient Stories
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 style={{ fontSize: "1.6rem" }}>
              Ready to Take Care of Your Heart Health?
            </h2>
            <p>
              Don't wait until it's too late. Schedule your consultation today
              and take the first step towards a healthier heart.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Book Your Appointment
              </Link>
              <a href={`tel:${HELPDESK_PHONE_TEL}`} className="btn btn-secondary">
                <FaPhone /> Call Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
